import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser, useChangePassword } from '@/hooks/useUsers';
import { useAuth } from '@/hooks/useAuth';
import { User, UserCreateRequest, UserUpdateRequest } from '@/types/api';
import { UserRole, PAGE_SLUGS } from '@/types/enums';

const UsersPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'navigation', 'common']);
  const { user: currentUser } = useAuth();
  const { data: users, isLoading, error } = useUsers();
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const deleteMutation = useDeleteUser();
  const changePasswordMutation = useChangePassword();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [passwordUser, setPasswordUser] = useState<User | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Form state
  const [formUsername, setFormUsername] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formRole, setFormRole] = useState<UserRole>(UserRole.REVIEWER);
  const [formAllowedPages, setFormAllowedPages] = useState<string[]>([]);
  const [useAllPages, setUseAllPages] = useState(true);

  // Password change state
  const [newPassword, setNewPassword] = useState('');

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormUsername('');
    setFormPassword('');
    setFormDisplayName('');
    setFormRole(UserRole.REVIEWER);
    setFormAllowedPages([]);
    setUseAllPages(true);
    setDialogOpen(true);
  };

  const openEditDialog = (user: User) => {
    setEditingUser(user);
    setFormUsername(user.username);
    setFormPassword('');
    setFormDisplayName(user.display_name || '');
    setFormRole(user.role as UserRole);
    setFormAllowedPages(user.allowed_pages || []);
    setUseAllPages(!user.allowed_pages);
    setDialogOpen(true);
  };

  const openPasswordDialog = (user: User) => {
    setPasswordUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleSave = async () => {
    const allowedPages = (formRole === UserRole.ADMIN || useAllPages) ? null : formAllowedPages;

    if (editingUser) {
      const data: UserUpdateRequest = {
        display_name: formDisplayName || undefined,
        role: formRole,
        allowed_pages: allowedPages as any,
      };
      if (formPassword) {
        data.password = formPassword;
      }
      await updateMutation.mutateAsync({ id: editingUser.id, data });
      showSuccess(t('pages:users.userUpdated'));
    } else {
      const data: UserCreateRequest = {
        username: formUsername,
        password: formPassword,
        display_name: formDisplayName || undefined,
        role: formRole,
        allowed_pages: allowedPages ?? undefined,
      };
      await createMutation.mutateAsync(data);
      showSuccess(t('pages:users.userCreated'));
    }
    setDialogOpen(false);
  };

  const handleDelete = async () => {
    if (!deletingUser) return;
    await deleteMutation.mutateAsync(deletingUser.id);
    setDeleteDialogOpen(false);
    setDeletingUser(null);
    showSuccess(t('pages:users.userDeleted'));
  };

  const handleChangePassword = async () => {
    if (!passwordUser || !newPassword) return;
    await changePasswordMutation.mutateAsync({ id: passwordUser.id, data: { new_password: newPassword } });
    setPasswordDialogOpen(false);
    setPasswordUser(null);
    setNewPassword('');
    showSuccess(t('pages:users.passwordChanged'));
  };

  const togglePage = (slug: string) => {
    setFormAllowedPages((prev) =>
      prev.includes(slug) ? prev.filter((p) => p !== slug) : [...prev, slug]
    );
  };

  const getPageLabel = (slug: string) => {
    const keyMap: Record<string, string> = {
      'dashboard': 'dashboard',
      'review': 'review',
      'index-channel': 'indexChannel',
      'timeline': 'timeline',
      'channels': 'channels',
      'libraries': 'libraries',
      'entries': 'entries',
      'chat-preview': 'chatPreview',
      'settings': 'settings',
    };
    return t(`navigation:${keyMap[slug] || slug}`);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('pages:users.title')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          {t('pages:users.addUser')}
        </Button>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load users
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('pages:users.username')}</TableCell>
              <TableCell>{t('pages:users.displayName')}</TableCell>
              <TableCell>{t('pages:users.role')}</TableCell>
              <TableCell>{t('pages:users.allowedPages')}</TableCell>
              <TableCell>{t('pages:users.created')}</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users && users.length > 0 ? (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Typography fontWeight="medium">{user.username}</Typography>
                  </TableCell>
                  <TableCell>{user.display_name || '—'}</TableCell>
                  <TableCell>
                    {/* Two different columns. `manuscript_role` is what grants
                        anything HERE (decision 2026-08-18); `role` is the ilm
                        dashboard's own tier and is shown for context only. */}
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      <Chip
                        label={user.manuscript_role ?? t('pages:users.noManuscriptAccess')}
                        color={user.manuscript_role === 'admin' ? 'error' : 'primary'}
                        size="small"
                      />
                      <Chip
                        label={`${t('pages:users.dashboardRole')}: ${user.role}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    {user.manuscript_role === 'admin' ? (
                      <Typography variant="body2" color="text.secondary">
                        {t('pages:users.allPages')}
                      </Typography>
                    ) : user.allowed_pages ? (
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {user.allowed_pages.map((slug) => (
                          <Chip key={slug} label={getPageLabel(slug)} size="small" variant="outlined" />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('pages:users.allPages')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openPasswordDialog(user)} title={t('pages:users.changePassword')}>
                      <KeyIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => openEditDialog(user)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={currentUser?.id === user.id}
                      onClick={() => {
                        setDeletingUser(user);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" py={4}>
                    {t('pages:users.noUsers')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? t('pages:users.editUser') : t('pages:users.addUser')}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label={t('pages:users.username')}
              value={formUsername}
              onChange={(e) => setFormUsername(e.target.value)}
              disabled={!!editingUser}
              fullWidth
              required
            />
            <TextField
              label={editingUser ? t('pages:users.newPassword') : t('pages:users.password')}
              type="password"
              value={formPassword}
              onChange={(e) => setFormPassword(e.target.value)}
              fullWidth
              required={!editingUser}
              helperText={editingUser ? t('pages:users.leaveBlankPassword') : undefined}
            />
            <TextField
              label={t('pages:users.displayName')}
              value={formDisplayName}
              onChange={(e) => setFormDisplayName(e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>{t('pages:users.role')}</InputLabel>
              <Select
                value={formRole}
                label={t('pages:users.role')}
                onChange={(e) => setFormRole(e.target.value as UserRole)}
              >
                <MenuItem value={UserRole.ADMIN}>Admin</MenuItem>
                <MenuItem value={UserRole.REVIEWER}>Reviewer</MenuItem>
              </Select>
            </FormControl>

            {formRole !== UserRole.ADMIN && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useAllPages}
                      onChange={(e) => setUseAllPages(e.target.checked)}
                    />
                  }
                  label={t('pages:users.allPages')}
                />

                {!useAllPages && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('pages:users.allowedPages')}
                    </Typography>
                    <FormGroup>
                      {PAGE_SLUGS.map((slug) => (
                        <FormControlLabel
                          key={slug}
                          control={
                            <Checkbox
                              checked={formAllowedPages.includes(slug)}
                              onChange={() => togglePage(slug)}
                            />
                          }
                          label={getPageLabel(slug)}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                )}
              </>
            )}

            {formRole === UserRole.ADMIN && (
              <Alert severity="info">{t('pages:users.adminFullAccess')}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common:actions.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formUsername || (!editingUser && !formPassword) || createMutation.isPending || updateMutation.isPending}
          >
            {t('common:actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('pages:users.changePassword')}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              {passwordUser?.username}
            </Typography>
            <TextField
              label={t('pages:users.newPassword')}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              required
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>{t('common:actions.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={!newPassword || changePasswordMutation.isPending}
          >
            {t('common:actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('pages:users.deleteUser')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('pages:users.confirmDelete', { username: deletingUser?.username })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common:actions.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {t('common:actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;
